import { Outlet, useNavigate, useParams } from "react-router-dom";
import React, { useEffect, createContext, useCallback, useRef, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useWebSocketRoom } from 'shared/hooks/useWebSocketRoom';
import { useDispenseManager } from "shared/hooks/useDispenseManager";
import { getDeviceCode } from "shared/utils/nativeBridge";

export const OutletContext = createContext();

function WebSocketContainer() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const unmountingRef = useRef(false);

    // 메시지 핸들러 생성 - useDispenseManager의 핸들러 참조 저장
    const messageHandlerRef = useRef(null);

    // 웹소켓 훅 사용 (메시지 핸들러 전달)
    const { isConnected, connectionStatus, messages, sendMessage } = useWebSocketRoom(id, payload => {
        if (messageHandlerRef.current) {
            messageHandlerRef.current(payload, navigate, id);
        }
    });

    // 배출 관련 기능 가져오기 (sendMessageFn 직접 전달)
    const {
        isRailActive,
        activeDevice,
        requestDeviceCode,
        activeOutlets,
        handleWebSocketMessage,
        notifyRailCompleted
    } = useDispenseManager(
        sendMessage, // 전송 함수 전달
        getDeviceCode,
        console.log
    );

    // handleWebSocketMessage 함수 참조 업데이트
    useEffect(() => {
        messageHandlerRef.current = handleWebSocketMessage;
    }, [handleWebSocketMessage]);

    // 마운트/언마운트 로깅 (디버깅용)
    useEffect(() => {
        console.log("WebSocketContainer: 컴포넌트 마운트됨");
        unmountingRef.current = false;

        return () => {
            unmountingRef.current = true;
            console.log("WebSocketContainer: 컴포넌트 언마운트됨");
        };
    }, []);

    // 전역 notifyRailCompleted 함수 등록 (기존 로직 유지)
    useEffect(() => {
        const prevNotifyRailCompleted = window.notifyRailCompleted;
        window.notifyRailCompleted = notifyRailCompleted;

        return () => {
            window.notifyRailCompleted = prevNotifyRailCompleted;
        };
    }, [notifyRailCompleted]);

    // 전역 sendMessage 함수 등록 (기존 로직 유지)
    useEffect(() => {
        const prevSendMessage = window.sendMessage;

        window.sendMessage = (message) => {
            if (!message) return false;
            return sendMessage(message);
        };

        return () => {
            window.sendMessage = prevSendMessage;
        };
    }, [sendMessage]);

    // Context 값 메모이제이션 (기존 로직 유지)
    const contextValue = useMemo(() => ({
        id,
        sendMessage, // 웹소켓 훅에서 제공하는 함수 사용
        isRailActive,
        activeDevice,
        requestDeviceCode,
        activeOutlets,
        notifyRailCompleted,
        connected: isConnected,
        connectionIdentifier: `${isConnected ? 'connected' : connectionStatus}`
    }), [
        id,
        sendMessage,
        isRailActive,
        activeDevice,
        requestDeviceCode,
        activeOutlets,
        notifyRailCompleted,
        isConnected,
        connectionStatus
    ]);

    return (
        <OutletContext.Provider value={contextValue}>
            <Outlet />
        </OutletContext.Provider>
    );
}

export default React.memo(WebSocketContainer);